import { useEffect, useRef, useState } from "react";
import LineTemplate from "./Components/LineTemplate";
import "./LinksComponentDashboard.css";
import { Button, Modal, Popover, Spin, Tooltip } from "antd";
import { useLoadUserLinks } from "./Utils/useLoadUserLinks";
import { deleteUserLinks } from "../../../../../Utils/SupabaseServices/UserLinksDatabase";
import { DragDropContext, Draggable, Droppable } from "react-beautiful-dnd";
import { UpdateUserLinksIndex } from "../../../../../Utils/SupabaseServices/UserLinksDatabase";
import { IoAddCircle } from "react-icons/io5";
import { BsPlusLg } from "react-icons/bs";
import { MyLinksSkeleton } from "../../../../../components/skeleton/DashboardView/myLinksSkeleton";
import DraggableTemplateGrid from "./Components/DraggableTemplateGrid";
import { isMobile } from "react-device-detect";
import { FiDownload } from "react-icons/fi";
import { useAuth } from "../../../../../hooks/useAuth";
import ImportLinksModal from "./ImportLinksModal";
import CustomDraggableTemplateGrid from "./Components/CustomDraggableTemplateGrid";
import {
  ensureUserLinksHaveLayoutCrop,
  linkNeedsDefaultLayoutCropCreation,
  withLayoutDisplayImage,
} from "../../../../../Utils/imageCrop/ensureLinkLayoutDefaultCrop";
import type { LinkCropLayout } from "../../../../../Utils/imageCrop/cropStatePersist";

const LinksComponentDashboard = (props: any) => {
  const { component, username, user_id, brand_color, from } = props;
  const { name, details } = component;
  const componentId = component?.id || null;
  const layout: LinkCropLayout = details === "grid" ? "grid" : "line";
  const { isAdmin } = useAuth();
  const { data, error, refetch } = useLoadUserLinks(
    user_id,
    name,
    from,
    componentId
  );
  const [editIndex, setEditIndex] = useState<any>();
  const [templates, setTemplates] = useState<any[]>([]);
  const [showPopover, setShowPopover] = useState(false);
  const [isLoading, setIsLoading] = useState(true);
  const [isNewCard, setIsNewCard] = useState(false);
  const [isMigrateModalOpen, setIsMigrateModalOpen] = useState(false);
  const [activeImageUploads, setActiveImageUploads] = useState(0);
  const [layoutCropProcessingIds, setLayoutCropProcessingIds] = useState<
    Set<any>
  >(() => new Set());
  const prevDetailsRef = useRef<string | undefined>(undefined);
  const layoutEnsureGenRef = useRef(0);

  useEffect(() => {
    if (data) {
      const sortedData = [...data].sort((a, b) => a.index - b.index);
      if (sortedData.length === 0) {
        setTemplates([
          {
            id: 0,
            link_name: "",
            link_url: "",
            image_url: null,
            original_image_url: null,
            index: 0,
          },
        ]);
      } else {
        // Immediately show each link's image for the active layout when present.
        setTemplates(
          sortedData.map((card) => withLayoutDisplayImage(card, layout))
        );
      }

      setIsLoading(false);
    }
  }, [data, layout]);

  // On grid ↔ line switch: reuse saved layout images; create defaults with loaders.
  useEffect(() => {
    const prev = prevDetailsRef.current;
    if (prev === undefined) {
      prevDetailsRef.current = details;
      return;
    }
    if (prev === details) return;
    if (details !== "grid" && details !== "line") {
      prevDetailsRef.current = details;
      return;
    }
    // Wait for links before advancing — otherwise a switch before data loads
    // would skip ensure forever.
    if (!data?.length) return;

    prevDetailsRef.current = details;
    const nextLayout: LinkCropLayout = details === "grid" ? "grid" : "line";
    const gen = ++layoutEnsureGenRef.current;
    const needingCreate = data.filter((link: any) =>
      linkNeedsDefaultLayoutCropCreation(link, nextLayout)
    );
    setLayoutCropProcessingIds(new Set(needingCreate.map((l: any) => l.id)));

    void ensureUserLinksHaveLayoutCrop({
      user_id,
      username,
      collectionName: name,
      from,
      componentId,
      layout: nextLayout,
      links: data,
      onCreating: (id) => {
        if (gen !== layoutEnsureGenRef.current) return;
        setLayoutCropProcessingIds((prevIds) => {
          const next = new Set(prevIds);
          next.add(id);
          return next;
        });
      },
      onSettled: (id, patch) => {
        if (gen !== layoutEnsureGenRef.current) return;
        setLayoutCropProcessingIds((prevIds) => {
          const next = new Set(prevIds);
          next.delete(id);
          return next;
        });
        if (patch) {
          setTemplates((prevTemplates) =>
            prevTemplates.map((card) =>
              card.id === id
                ? {
                    ...card,
                    image_url: patch.image_url,
                    image_crop: patch.image_crop ?? card.image_crop,
                  }
                : card
            )
          );
        }
      },
    }).then(() => {
      if (gen === layoutEnsureGenRef.current) {
        void refetch();
      }
    });
  }, [details, data, user_id, username, name, from, componentId, refetch]);

  const handleAddTemplate = () => {
    const hasEmptyTemplate = templates.some(
      (template: any) => template.id === 0
    );
    setIsNewCard(true);
    if (hasEmptyTemplate) {
      setShowPopover(true);
      setTimeout(() => setShowPopover(false), 3000);
      return;
    }
    setEditIndex(undefined);
    const newTemplate = {
      id: 0,
      link_name: "",
      link_url: "",
      image_url: null,
      original_image_url: null,
      index: templates.length,
    };
    setTemplates([...templates, newTemplate]);
    setTimeout(() => {
      setIsNewCard(false);
    }, 300);
  };

  const handleRemoveTemplate = async (id: any) => {
    // setIsLoading(true);
    const updatedTemplates = templates.filter(
      (template: any) => template.id !== id
    );
    setTemplates(updatedTemplates);
    setEditIndex(undefined);
    setIsNewCard(false);
    await deleteUserLinks(user_id, name, id, from, componentId);
    // setTimeout(() => {
    //   setIsLoading(false);
    // }, 300);
  };

  const onDragEnd = (result: any) => {
    if (!result.destination) return;

    const newTemplates = Array.from(templates);
    const [reorderedItem] = newTemplates.splice(result.source.index, 1);
    newTemplates.splice(result.destination.index, 0, reorderedItem);

    UpdateUserLinksIndex(newTemplates, user_id, name, from, componentId);
    setTemplates(newTemplates);
  };

  const isEmptyTemplate = templates.some(
    (template: any) =>
      template.id === 0 && !template.link_name && !template.link_url
  );

  const handleReorder = (newOrder: any) => {
    const reorderedTemplates = newOrder.map((template: any, index: number) => ({
      ...template,
      index: index,
    }));

    UpdateUserLinksIndex(
      reorderedTemplates,
      user_id,
      name,
      from,
      componentId
    );
    setTemplates(reorderedTemplates);
  };

  const handleImageUploadStatusChange = (isUploading: boolean) => {
    setActiveImageUploads((prev) =>
      isUploading ? prev + 1 : Math.max(0, prev - 1)
    );
  };

  const isImageUploading = activeImageUploads > 0;

  // Migrate functionality
  const ImportButton = () => {
    return (
      <>
      {isAdmin && <button
        className="migrate-button"
        onClick={() => setIsMigrateModalOpen(true)}
        aria-label="Import links from a page"
      >
        <FiDownload className="migrate-button-icon" />
        <span className="migrate-button-text">
          Import from Page
        </span>
      </button>}
      </>
    );
  };

  return (
    <>
      {isLoading ? (
        <div className="loader-container">
          <MyLinksSkeleton
            templateType={details === "grid" ? "grid" : "line"}
            count={3}
            matchContentLayout={details === "grid"}
            isDashboard
          />
        </div>
      ) : details === "grid" ? (
        <div data-tour="dashboard-my-links">
          <div
            className={
              details === "grid" ? "creator-card-grid-wrp-subcomponents" : ""
            }
          >
            {/* <DraggableTemplateGrid */}
            <CustomDraggableTemplateGrid
              // templates={templates}
              // card={card}
              onRemove={handleRemoveTemplate}
              username={username}
              user_id={user_id}
              componentName={name}
              isDashboard={true}
              refetch={refetch}
              editIndex={editIndex}
              setEditIndex={setEditIndex}
              brand_color={brand_color}
              templates={templates}
              showDelete={!isEmptyTemplate}
              handleReorder={handleReorder}
              from={from}
              componentId={componentId}
              onImageUploadStatusChange={handleImageUploadStatusChange}
              layoutCropProcessingIds={layoutCropProcessingIds}
            />
          </div>
          <div className="add-button-line-container">
            <Popover
              open={showPopover}
              content={
                <div style={{
                  maxWidth: isMobile ? "250px" : "300px",
                  wordWrap: "break-word",
                  whiteSpace: "normal",
                  fontSize: isMobile ? "12px" : "14px",
                  lineHeight: "1.4",
                  padding: isMobile ? "4px 0" : "8px 0",
                }}>
                  Please complete the current template before adding a new one
                </div>
              }
              placement={isMobile ? "topLeft" : "top"}
              overlayStyle={{
                maxWidth: isMobile ? "280px" : "320px",
              }}
            >
              <button 
                className="add-to-page-button"
                onClick={handleAddTemplate}
                aria-label="Add New Link"
                disabled={isImageUploading}
              >
                {isImageUploading ? (
                  <Spin size="small" style={{ marginRight: "10px", color: "white" }} />
                ) : (
                  <BsPlusLg className="add-button-icon" />
                )}
                <span className="add-button-text">
                  {isImageUploading ? "Uploading image..." : "Add New Link"}
                </span>
              </button>
            </Popover>
            <ImportButton />
          </div>
        </div>
      ) : (
        <DragDropContext onDragEnd={onDragEnd}>
          <Droppable
            droppableId="template-list"
            direction={details === "grid" ? "horizontal" : "vertical"}
          >
            {(provided) => (
              <div data-tour="dashboard-my-links">
                <div
                  className={
                    details === "grid"
                      ? "creator-card-grid-wrp-subcomponents"
                      : ""
                  }
                  {...provided.droppableProps}
                  ref={provided.innerRef}
                  style={{
                    paddingLeft: "1rem",
                    paddingRight: "1rem",
                    paddingTop: "1rem",
                  }}
                >
                  {templates.map((card: any, index: any) => (
                    <Draggable
                      key={card?.id}
                      draggableId={card?.id?.toString()}
                      index={index}
                      // isDragDisabled
                    >
                      {(provided, snapshot) => {
                        return (
                          <div
                            ref={provided.innerRef}
                            {...provided.draggableProps}
                            style={{
                              ...(details === "line"
                                ? {
                                    width: "100%",
                                    display: "flex",
                                    justifyContent: "center",
                                  }
                                : {}),
                              ...provided.draggableProps.style,
                            }}
                          >
                           
                            <div
                              className={`links-container ${
                                details === "grid"
                                  ? "grid-layout"
                                  : "line-layout"
                              }`}
                            >
                              <LineTemplate
                                card={card}
                                onRemove={handleRemoveTemplate}
                                username={username}
                                user_id={user_id}
                                componentName={name}
                                isDashboard={true}
                                refetch={refetch}
                                editIndex={editIndex}
                                setEditIndex={setEditIndex}
                                brand_color={brand_color}
                                templates={templates}
                                showDelete={!isEmptyTemplate}
                                from={from}
                                componentId={componentId}
                                isDragging={snapshot.isDragging}
                                dragHandleProps={provided.dragHandleProps}
                                onImageUploadStatusChange={handleImageUploadStatusChange}
                                isLayoutCropProcessing={layoutCropProcessingIds.has(
                                  card.id
                                )}
                              />
                            </div>
                            {/* )} */}
                          </div>
                        );
                      }}
                    </Draggable>
                  ))}

                  {provided.placeholder}
                </div>
                <div className="add-button-line-container">
                  <Popover
                    open={showPopover}
                    content={
                      <div style={{
                        maxWidth: isMobile ? "250px" : "300px",
                        wordWrap: "break-word",
                        whiteSpace: "normal",
                        fontSize: isMobile ? "12px" : "14px",
                        lineHeight: "1.4",
                        padding: isMobile ? "4px 0" : "8px 0",
                      }}>
                        Please complete the current template before adding a new one
                      </div>
                    }
                    placement={isMobile ? "topLeft" : "top"}
                    overlayStyle={{
                      maxWidth: isMobile ? "280px" : "320px",
                    }}
                  >
                    <button 
                      className="add-to-page-button"
                      onClick={handleAddTemplate}
                      aria-label="Add New Link"
                      disabled={isImageUploading}
                    >
                      {isImageUploading ? (
                        <Spin size="small" style={{ marginRight: "10px", color: "white" }} />
                      ) : (
                        <BsPlusLg className="add-button-icon" />
                      )}
                      <span className="add-button-text">
                        {isImageUploading ? "Uploading image..." : "Add New Link"}
                      </span>
                    </button>
                  </Popover>
                  <ImportButton />
                </div>
              </div>
            )}
          </Droppable>
        </DragDropContext>
      )}

      {/* Migrate Modal */}
      {isMigrateModalOpen && (
        <ImportLinksModal
          isMigrateModalOpen={isMigrateModalOpen}
          setIsMigrateModalOpen={setIsMigrateModalOpen}
          user_id={user_id}
          username={username}
          name={name}
          from={from}
          componentId={componentId}
          refetch={refetch}
        />
      )}


    </>
  );
};

export default LinksComponentDashboard;

export const DeleteConfirmation = (props: any) => {
  const { setShowDeleteConfirm, setEditIndex, handleDelete } = props;

  const onCancel = () => {
    setShowDeleteConfirm(false);
    setEditIndex(undefined);
  };

  const onOk = () => {
    handleDelete();
  };

  return (
    <Modal
      title="Delete item"
      open={true}
      centered
      onCancel={onCancel}
      onOk={onOk}
      okText="Delete"
      cancelText="Cancel"
      okButtonProps={{ danger: true, className: "links-delete-confirm-btn" }}
      getContainer={() =>
        document.getElementById("root") ?? document.body
      }
      focusTriggerAfterClose={false}
      zIndex={10050}
      className="links-delete-confirm-modal"
    >
      <p className="links-delete-confirm-text">Are you sure you want to delete this item?</p>
    </Modal>
  );
};
